/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";

/**
 * Long-form content for each blog article (slug → JSX).
 * Each article is original, SEO-friendly Spanish content (~1,500 words).
 * Uses prose-article styling defined in App.css.
 */

const Callout = ({ children }) => (
  <div className="my-7 p-5 border-l-4 border-[#bc6c25] bg-[#bc6c25]/5 rounded-r-2xl text-[#1a2421] text-[15px] leading-relaxed">
    {children}
  </div>
);

const Quote = ({ children, author }) => (
  <blockquote className="my-7 pl-6 border-l-2 border-[#2c4a3b]/30 italic text-[#1a2421] text-lg leading-relaxed font-serif">
    "{children}"
    {author && <footer className="mt-2 not-italic text-xs uppercase tracking-[0.18em] text-[#5c6b61]">— {author}</footer>}
  </blockquote>
);

const TOC = ({ items }) => (
  <nav className="my-7 p-5 bg-[#f0ebe1] rounded-2xl border border-[#2c4a3b]/8 not-prose">
    <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">En este artículo</p>
    <ol className="mt-3 space-y-1.5 text-sm text-[#1a2421]">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2">
          <span className="text-[#bc6c25] font-bold">{i + 1}.</span>
          <a href={`#${it.id}`} className="hover:text-[#bc6c25]">{it.label}</a>
        </li>
      ))}
    </ol>
  </nav>
);

export const ARTICLE_CONTENT = {
  /* =================================================================
     1. ¿Qué es el glutatión y por qué es el antioxidante maestro?
     ================================================================= */
  "que-es-el-glutation-antioxidante-maestro": (
    <>
      <p>Si tuvieras que elegir <strong>una sola molécula</strong> para mantener tu cuerpo joven, resistente y libre de enfermedad, los científicos coincidirían en una respuesta: el <strong>glutatión</strong>. Es la sustancia más estudiada en medicina antienvejecimiento, y sin embargo, la mayoría de las personas nunca ha oído hablar de ella.</p>

      <p>En este artículo te voy a explicar —de forma clara y con respaldo científico— qué es el glutatión, por qué se le llama "el antioxidante maestro", qué pasa cuando tus niveles bajan, y cómo puedes optimizarlo con cambios reales en tu día a día.</p>

      <TOC items={[
        { id: "que-es", label: "Qué es exactamente el glutatión" },
        { id: "antioxidante-maestro", label: "Por qué se le llama el antioxidante maestro" },
        { id: "funciones", label: "Las 3 funciones críticas del GSH" },
        { id: "cuando-baja", label: "Qué pasa cuando tus niveles bajan" },
        { id: "elevarlo", label: "Cómo elevar tus niveles (de forma realista)" },
        { id: "immunocal", label: "Por qué Immunocal es la opción más estudiada" },
        { id: "conclusion", label: "Cómo empezar hoy" },
      ]} />

      <h2 id="que-es">Qué es exactamente el glutatión</h2>
      <p>El glutatión (abreviado GSH, por su nombre químico <em>glutatión reducido</em>) es un <strong>tripéptido</strong>, es decir, una molécula pequeña compuesta por tres aminoácidos: cisteína, ácido glutámico y glicina. Tu cuerpo lo produce de forma natural dentro de cada célula. No se obtiene "tomándolo" directamente, sino aportando a tu organismo los <strong>precursores</strong> correctos —especialmente cisteína biodisponible— para que las células lo fabriquen.</p>

      <p>El glutatión existe en todos los tejidos: hígado, cerebro, pulmones, riñones, corazón, piel y sistema inmune. Las concentraciones más altas están en el <strong>hígado</strong>, donde tiene un papel central en la detoxificación.</p>

      <Callout>
        <strong>Dato clave:</strong> los niveles intracelulares de glutatión empiezan a descender de forma medible <strong>después de los 30 años</strong>, y caen entre 8 % y 12 % por década. A los 65 años, los niveles pueden ser hasta un 40 % más bajos que en la juventud.
      </Callout>

      <h2 id="antioxidante-maestro">Por qué se le llama "el antioxidante maestro"</h2>
      <p>Probablemente has escuchado hablar de la vitamina C, la vitamina E, el ácido alfa-lipoico o el resveratrol como antioxidantes. Todos lo son. Pero el glutatión es diferente por tres razones que lo elevan a una categoría aparte:</p>

      <h3>1. Es universal e intracelular</h3>
      <p>La mayoría de antioxidantes trabaja fuera de las células o en compartimentos específicos. El glutatión está <strong>dentro de cada célula</strong>, en el citoplasma y dentro de las mitocondrias (donde se genera el 95 % del estrés oxidativo). Es la primera línea de defensa <em>donde más se necesita</em>.</p>

      <h3>2. Regenera a los demás antioxidantes</h3>
      <p>Cuando la vitamina C neutraliza un radical libre, ella misma se oxida y deja de funcionar. ¿Quién la "recicla"? El glutatión. Lo mismo hace con la vitamina E y el ácido alfa-lipoico. Sin glutatión adecuado, los demás antioxidantes pierden eficacia.</p>

      <h3>3. Es co-factor enzimático</h3>
      <p>El glutatión activa enzimas clave del cuerpo, como la <strong>glutatión peroxidasa</strong> (neutraliza peróxidos) y la <strong>glutatión-S-transferasa</strong> (detoxifica xenobióticos en el hígado).</p>

      <h2 id="funciones">Las 3 funciones críticas del GSH</h2>
      <p>Más allá de "antioxidante", el glutatión cumple tres roles que la mayoría de personas desconoce:</p>

      <h3>1. Defensa antioxidante directa</h3>
      <p>Neutraliza radicales libres, peróxido de hidrógeno y especies reactivas de oxígeno (ROS). Sin esta defensa, las membranas celulares, las proteínas y el ADN sufren daño constante —el sustrato bioquímico del envejecimiento y de muchas enfermedades crónicas.</p>

      <h3>2. Detoxificación hepática (Fase II)</h3>
      <p>El hígado tiene dos fases de detoxificación. En la Fase II, el glutatión <strong>conjuga</strong> (se une a) toxinas, metales pesados, restos de medicamentos y contaminantes para volverlos solubles en agua y excretarlos por la orina o la bilis. Sin glutatión suficiente, estas toxinas se acumulan en tejidos.</p>

      <h3>3. Soporte inmune</h3>
      <p>Los linfocitos T necesitan glutatión para proliferar y eliminar células infectadas o cancerosas. Un sistema inmune funcional <strong>depende</strong> de glutatión adecuado. Esta es una de las razones por las que personas con niveles bajos enferman con más frecuencia y gravedad.</p>

      <Quote author="Dr. Mark Hyman, medicina funcional">
        El glutatión es la madre de todos los antioxidantes. Es el secreto mejor guardado de la salud celular.
      </Quote>

      <h2 id="cuando-baja">Qué pasa cuando tus niveles bajan</h2>
      <p>Cuando el glutatión cae —por edad, estrés, infecciones, mala alimentación o exposición a tóxicos—, los síntomas no aparecen de golpe. Son <strong>graduales</strong> y suelen confundirse con "envejecimiento normal":</p>
      <ul>
        <li>Fatiga persistente que no mejora con descanso.</li>
        <li>Infecciones respiratorias frecuentes.</li>
        <li>Recuperación lenta después de entrenar.</li>
        <li>Mayor sensibilidad a contaminación, alcohol o medicamentos.</li>
        <li>Dolor articular o muscular inflamatorio.</li>
        <li>Piel apagada, manchas, envejecimiento visible.</li>
        <li>Dificultad de concentración y "niebla mental".</li>
      </ul>
      <p>Niveles crónicamente bajos están asociados —según estudios revisados por pares— con enfermedades como Alzheimer, Parkinson, enfermedad cardiovascular, diabetes tipo 2, EPOC, hepatitis crónica y varios tipos de cáncer.</p>

      <h2 id="elevarlo">Cómo elevar tus niveles (de forma realista)</h2>
      <p>Aquí está la parte clave: <strong>tomar glutatión por boca no funciona bien</strong>. La molécula se degrada en el estómago. Lo que sí funciona es darle a tu cuerpo lo que necesita para fabricarlo:</p>

      <ol>
        <li><strong>Aporta cisteína bioactiva.</strong> Es el aminoácido limitante. La mejor fuente alimentaria son las proteínas no desnaturalizadas del suero de leche (ver Immunocal abajo). El huevo crudo o cocinado suave también aporta cisteína, pero en menor concentración.</li>
        <li><strong>Come alimentos ricos en azufre.</strong> Ajo, cebolla, brócoli, coliflor, col rizada, espárragos. El azufre es esencial para los grupos sulfhidrilo (-SH) que dan al glutatión su poder antioxidante.</li>
        <li><strong>Cuida tu hígado.</strong> Reduce alcohol, ultraprocesados y exceso de fármacos. Cada toxina consume glutatión.</li>
        <li><strong>Duerme entre 7 y 9 horas.</strong> El cuerpo reconstituye sus reservas durante el sueño profundo.</li>
        <li><strong>Haz ejercicio moderado.</strong> El movimiento sostenido aumenta la capacidad antioxidante. El ejercicio extremo, en cambio, la <em>vacía</em>.</li>
        <li><strong>Suplementa con cofactores.</strong> Selenio, vitamina C, magnesio y vitaminas del complejo B son co-factores del ciclo del glutatión.</li>
      </ol>

      <h2 id="immunocal">Por qué Immunocal es la opción más estudiada</h2>
      <p><Link to="/immunocal">Immunocal</Link> es una proteína aislada de suero de leche no desnaturalizada, patentada por el Dr. Gustavo Bounous tras décadas de investigación en la Universidad McGill (Canadá). Es la <strong>única proteína</strong> con patente médica para elevar glutatión intracelular, con más de <strong>40 estudios revisados por pares</strong>.</p>
      <p>Su mecanismo: aporta cisteína en forma de enlaces disulfuro intactos. Esos enlaces son frágiles —cualquier whey común los pierde con calor o filtración agresiva—. Immunocal preserva esa conformación con un proceso especial a baja temperatura, lo que permite que la cisteína llegue íntegra a las células.</p>

      <Callout>
        <strong>Diferencia clave:</strong> una whey común de gimnasio aporta proteína para construir músculo. Immunocal hace eso <em>y</em> eleva glutatión intracelular. Si quieres ver el detalle, lee <Link to="/blog/immunocal-vs-proteina-whey">Immunocal vs proteína whey común: 7 diferencias clave</Link>.
      </Callout>

      <h2 id="conclusion">Cómo empezar hoy</h2>
      <p>Si estás leyendo esto, lo más probable es que tus niveles de glutatión no estén en óptimo. No por algo grave —simplemente porque vives en el siglo XXI: contaminación, estrés, mal sueño y comida industrial son la norma. La buena noticia es que <strong>se puede revertir</strong>.</p>
      <p>Mi recomendación:</p>
      <ol>
        <li>Haz <Link to="/#quiz">el quiz wellness gratuito</Link> de Eliora Longevity. Te da un protocolo de 3 meses personalizado en 3 minutos.</li>
        <li>Empieza con Immunocal Original (1 sobre al día en agua fría, en ayunas).</li>
        <li>Suma hábitos de soporte: sol matutino, alimentos azufrados, sueño consistente.</li>
        <li>Dale a tu cuerpo <strong>al menos 90 días</strong>. La regeneración celular toma tiempo, pero las personas que sostienen el protocolo reportan cambios visibles en energía, recuperación y claridad mental.</li>
      </ol>
      <p>Para profundizar, te invito a leer nuestra <Link to="/glutation">guía completa de glutatión</Link> y la <Link to="/immunocal">guía de Immunocal</Link>.</p>
      <p>Tu cuerpo ya sabe fabricar el antioxidante maestro. Tu trabajo es <strong>darle las piezas</strong>.</p>
    </>
  ),

  /* =================================================================
     2. Immunocal vs proteína whey común: 7 diferencias clave
     ================================================================= */
  "immunocal-vs-proteina-whey": (
    <>
      <p>Una de las preguntas que más me hacen como Distribuidora Independiente Immunotec es: <em>"Sarai, ¿no es Immunocal básicamente una whey carísima?"</em>. Lo entiendo. En el supermercado puedes comprar una whey de 2 kg por menos de lo que cuestan 30 sobres de Immunocal. Pero estarías comparando <strong>peras con manzanas</strong>.</p>
      <p>Vamos a verlo claro. Estas son las 7 diferencias clave entre Immunocal y una proteína whey común, basadas en ciencia, no en marketing.</p>

      <TOC items={[
        { id: "extraccion", label: "1. Proceso de extracción" },
        { id: "cisteina", label: "2. Cisteína bioactiva intacta" },
        { id: "patente", label: "3. Patente y respaldo científico" },
        { id: "proposito", label: "4. Propósito de diseño" },
        { id: "aditivos", label: "5. Cero aditivos" },
        { id: "lactoferrina", label: "6. Lactoferrina activa" },
        { id: "digestion", label: "7. Tolerancia digestiva" },
        { id: "cual-elegir", label: "Cuál elegir según tu objetivo" },
      ]} />

      <h2 id="extraccion">1. Proceso de extracción</h2>
      <p>Una whey común se extrae con métodos rápidos y económicos: calor alto, filtración agresiva, secado por aspersión. Todo esto <strong>desnaturaliza</strong> las proteínas: literalmente "rompe" su forma 3D. Para construir músculo eso es suficiente, porque el cuerpo solo necesita los aminoácidos sueltos.</p>
      <p>Immunocal se extrae a <strong>baja temperatura</strong> con un proceso patentado que preserva la conformación nativa de las proteínas. Esto importa porque los <em>enlaces disulfuro</em> intactos son los que aportan cisteína bioactiva.</p>

      <h2 id="cisteina">2. Cisteína bioactiva intacta</h2>
      <p>La cisteína es el aminoácido limitante en la síntesis de glutatión. La mayoría de proteínas alimentarias la contiene, pero <em>oxidada</em>, lo que la hace prácticamente inservible. Solo la cisteína en su forma <strong>disulfuro intacto</strong> llega a la célula y se convierte en glutatión.</p>
      <p>Immunocal preserva ese formato. Una whey común no. Es como la diferencia entre madera fresca y aserrín: técnicamente "es lo mismo", pero solo una sirve para construir.</p>

      <Callout>
        <strong>Pregunta de prueba:</strong> ¿el bote de whey del gimnasio dice algo de "no desnaturalizada" o "enlaces disulfuro"? Si no, no estás comprando lo mismo.
      </Callout>

      <h2 id="patente">3. Patente y respaldo científico</h2>
      <p>Immunocal tiene <strong>patentes médicas en más de 36 países</strong> como precursor de glutatión. Está listado en el PDR (Physician's Desk Reference) de Estados Unidos y en el CPS (Compendium of Pharmaceuticals and Specialties) de Canadá —el equivalente del vademécum médico—.</p>
      <p>Hay más de <strong>40 estudios revisados por pares</strong> publicados sobre Immunocal, en revistas como <em>The Lancet</em>, <em>Anticancer Research</em>, <em>Journal of Cancer Research and Clinical Oncology</em> y <em>HIV Clinical Trials</em>. Las áreas estudiadas incluyen cáncer, VIH, EPOC, fibrosis quística, rendimiento atlético y envejecimiento.</p>
      <p>Una whey común tiene exactamente <strong>0</strong> publicaciones médicas equivalentes. No es un fracaso del producto: simplemente no está diseñado con propósito clínico.</p>

      <h2 id="proposito">4. Propósito de diseño</h2>
      <ul>
        <li><strong>Whey común:</strong> diseñada por marcas de suplementos deportivos para construir músculo y maximizar saciedad. Foco en gramos de proteína al menor costo posible.</li>
        <li><strong>Immunocal:</strong> diseñada por el Dr. Gustavo Bounous en McGill, originalmente como terapia adyuvante para pacientes oncológicos y con VIH. Foco en función inmune y antioxidante.</li>
      </ul>
      <p>Esto no significa que Immunocal no construya músculo —lo hace, y muy bien— pero ese es un beneficio <em>secundario</em>, no el principal.</p>

      <h2 id="aditivos">5. Cero aditivos artificiales</h2>
      <p>Si lees la etiqueta de una whey común, encontrarás: sucralosa, acesulfamo K, sabores artificiales, espesantes, colorantes, lecitina de soya, a veces gluten oculto. Todo eso suma <strong>carga inflamatoria</strong> al cuerpo.</p>
      <p>Immunocal: 100 % proteína aislada no desnaturalizada de suero de leche. Cero edulcorantes, cero saborizantes, cero rellenos. Por eso su sabor es neutro-suave y se mezcla con agua fría sin batidora.</p>

      <h2 id="lactoferrina">6. Lactoferrina activa preservada</h2>
      <p>La <strong>lactoferrina</strong> es una glicoproteína del suero con poderoso efecto antimicrobiano natural. Se une al hierro libre (que las bacterias necesitan para multiplicarse) y "se lo quita". Una whey común la pierde durante el procesamiento. Immunocal la conserva activa.</p>
      <p>Esto contribuye al efecto inmunomodulador que distingue al producto, especialmente útil en temporadas de gripe o en personas con infecciones recurrentes.</p>

      <h2 id="digestion">7. Tolerancia digestiva superior</h2>
      <p>Muchas personas que no toleran whey común sí toleran Immunocal. ¿Por qué?</p>
      <ul>
        <li>Es proteína <strong>aislada</strong> (no concentrada), con &lt;1 g de lactosa por sobre.</li>
        <li>Los péptidos están en fragmentos cortos, fáciles de digerir.</li>
        <li>No tiene aditivos que irriten el intestino.</li>
      </ul>
      <p>Por eso es bien tolerada incluso por personas con intolerancia leve a la lactosa, niños y adultos mayores.</p>

      <h2 id="cual-elegir">Cuál elegir según tu objetivo</h2>
      <p>Aquí mi recomendación honesta:</p>
      <Callout>
        <strong>Si tu único objetivo es ganar masa muscular y no quieres invertir más:</strong> una whey económica de calidad razonable cumple. Mide tus macros y entrena duro.
        <br /><br />
        <strong>Si quieres salud profunda, soporte inmune, recuperación, antienvejecimiento, o estás en una etapa de prevención/recuperación:</strong> Immunocal no tiene equivalente directo. Y a largo plazo, prevenir enfermedad sale infinitamente más barato que tratarla.
      </Callout>

      <p>Para tomar una decisión informada, lee también <Link to="/blog/que-es-el-glutation-antioxidante-maestro">¿Qué es el glutatión?</Link> y nuestra <Link to="/immunocal">guía completa de Immunocal</Link>.</p>
      <p>Si quieres saber el costo real y dónde conseguirlo con descuento de afiliado, te dejé toda la info en <Link to="/blog/immunocal-precio-mexico-2026">Immunocal precio México 2026</Link>.</p>
      <p>Y si quieres una recomendación personalizada (qué presentación te conviene, en qué dosis y con qué combinarlo), <Link to="/#quiz">haz el quiz wellness gratuito</Link>. Tarda 3 minutos y te devuelve un protocolo de 3 meses pensado para ti.</p>
    </>
  ),

  /* =================================================================
     3. Estrés oxidativo: cómo daña tu cuerpo y cómo combatirlo
     ================================================================= */
  "estres-oxidativo-explicado": (
    <>
      <p>Hay un proceso silencioso que ocurre dentro de ti <strong>cada segundo de tu vida</strong>. No lo sientes. No lo ves en el espejo (al principio). Pero es uno de los principales responsables del envejecimiento, la inflamación crónica y enfermedades como Alzheimer, diabetes, cáncer y cardiopatía.</p>
      <p>Se llama <strong>estrés oxidativo</strong>. Y entenderlo —y aprender a frenarlo— es probablemente la decisión de salud con más retorno de inversión que puedas tomar este año.</p>

      <TOC items={[
        { id: "que-es", label: "Qué es el estrés oxidativo, explicado simple" },
        { id: "radicales", label: "Radicales libres: el enemigo en cuestión" },
        { id: "causas", label: "De dónde viene en la vida moderna" },
        { id: "daño", label: "Cómo daña tu cuerpo (por sistema)" },
        { id: "señales", label: "Señales tempranas de que lo tienes alto" },
        { id: "estrategia", label: "Estrategia integral para combatirlo" },
        { id: "suplementos", label: "Los suplementos que sí funcionan" },
      ]} />

      <h2 id="que-es">Qué es el estrés oxidativo, explicado simple</h2>
      <p>Imagina una manzana cortada al aire. En minutos se pone marrón. Eso es oxidación: el oxígeno <em>roba</em> electrones a las células del fruto y las daña. Lo mismo pasa dentro de tu cuerpo, todo el tiempo. La diferencia es que tu organismo tiene un sistema antioxidante para defenderse.</p>
      <p>El <strong>estrés oxidativo</strong> es el desequilibrio entre dos fuerzas:</p>
      <ul>
        <li><strong>Radicales libres</strong> que oxidan (dañan) tus células.</li>
        <li><strong>Antioxidantes</strong> que neutralizan a esos radicales.</li>
      </ul>
      <p>Cuando hay más radicales que antioxidantes, tus tejidos —membranas celulares, proteínas, ADN, mitocondrias— sufren daño acumulativo. Y ese daño es la <strong>base bioquímica</strong> de casi toda enfermedad crónica.</p>

      <h2 id="radicales">Radicales libres: el enemigo en cuestión</h2>
      <p>Un radical libre es una molécula con un electrón desapareado. Es muy reactiva: "necesita" un electrón con urgencia y se lo arranca a la molécula más cercana. Esa molécula —ahora dañada— se convierte a su vez en radical, generando una <strong>reacción en cadena</strong> que puede dañar miles de moléculas antes de ser detenida.</p>
      <p>Los radicales libres no son del todo malos. En dosis controladas, tu sistema inmune los usa para destruir virus y bacterias. El problema es <strong>el exceso</strong>.</p>

      <h2 id="causas">De dónde viene el estrés oxidativo en la vida moderna</h2>
      <p>Internas (inevitables):</p>
      <ul>
        <li>Respiración celular en las mitocondrias (genera ~2 % de ROS de subproducto).</li>
        <li>Inflamación crónica de bajo grado.</li>
        <li>Respuesta inmune activa.</li>
      </ul>
      <p>Externas (modificables, y aquí está tu palanca):</p>
      <ul>
        <li>Contaminación del aire (PM2.5, NOx, ozono).</li>
        <li>Tabaco (activo o pasivo).</li>
        <li>Alcohol (cada gramo consume glutatión hepático).</li>
        <li>Ultraprocesados, azúcares refinados, aceites vegetales oxidados.</li>
        <li>Pesticidas, plásticos (BPA, ftalatos), metales pesados.</li>
        <li>Estrés psicológico crónico (eleva cortisol y citoquinas).</li>
        <li>Sueño insuficiente o de baja calidad.</li>
        <li>Ejercicio extremo sin recuperación adecuada.</li>
        <li>Exposición prolongada y sin protección al sol UV.</li>
        <li>Medicamentos (paracetamol, antibióticos, quimioterapia).</li>
      </ul>

      <h2 id="daño">Cómo daña tu cuerpo (por sistema)</h2>

      <h3>Cerebro</h3>
      <p>El cerebro consume el 20 % del oxígeno corporal pero solo pesa el 2 %. Es altamente vulnerable a oxidación. El daño acumulado a neuronas y a la mielina es uno de los factores clave en deterioro cognitivo, Alzheimer y Parkinson.</p>

      <h3>Cardiovascular</h3>
      <p>El LDL oxidado es el verdadero villano de la aterosclerosis, no el colesterol en sí. Cuando el LDL se oxida, se vuelve "pegajoso" en las paredes arteriales, iniciando la formación de placa.</p>

      <h3>Piel</h3>
      <p>Arrugas, manchas solares, flacidez y pérdida de luminosidad son <em>casi todos</em> resultado de daño oxidativo a las fibras de colágeno y elastina por exposición UV y contaminación.</p>

      <h3>Mitocondrias</h3>
      <p>Las "centrales energéticas" de tus células son particularmente sensibles. Mitocondrias dañadas = menos energía, más fatiga, menos capacidad de detoxificar.</p>

      <h3>ADN</h3>
      <p>El estrés oxidativo daña el ADN y puede iniciar procesos cancerosos. El cuerpo tiene reparadores muy efectivos, pero <em>requieren</em> antioxidantes funcionales para operar.</p>

      <h2 id="señales">Señales tempranas de que tu estrés oxidativo está alto</h2>
      <ul>
        <li>Fatiga matutina, aún durmiendo 8 horas.</li>
        <li>Recuperación lenta después de entrenar.</li>
        <li>Resfriados o gripas frecuentes.</li>
        <li>Manchas en la piel antes de tiempo.</li>
        <li>Niebla mental, dificultad de concentración.</li>
        <li>Dolor articular o muscular crónico.</li>
        <li>Sensibilidad creciente al alcohol o medicamentos.</li>
        <li>Cabello quebradizo, uñas débiles.</li>
      </ul>
      <p>Ninguna de estas señales es "grave" por sí sola. Pero juntas son una <strong>alarma sistémica</strong>.</p>

      <h2 id="estrategia">Estrategia integral para combatirlo</h2>
      <p>No hay un suplemento mágico. La estrategia eficaz tiene 4 frentes:</p>

      <h3>1. Reducir la entrada de radicales</h3>
      <ul>
        <li>Filtra tu agua (carbón activado mínimo).</li>
        <li>Ventila bien tus espacios cerrados.</li>
        <li>Evita ultraprocesados con aceites oxidados.</li>
        <li>Reduce alcohol al mínimo.</li>
        <li>Usa protector solar mineral (zinc) en exposición prolongada.</li>
      </ul>

      <h3>2. Aumentar antioxidantes endógenos</h3>
      <p>Tu cuerpo fabrica sus propios antioxidantes: glutatión, SOD (superóxido dismutasa), catalasa. Aporta los precursores (cisteína, selenio, zinc, manganeso). La fuente más estudiada de cisteína bioactiva es <Link to="/immunocal">Immunocal</Link>.</p>

      <h3>3. Sumar antioxidantes exógenos</h3>
      <p>Frutas y verduras coloridas, especias (cúrcuma, jengibre, canela), té verde, café negro, chocolate amargo &gt;80 %, frutos rojos. Polifenoles, flavonoides y carotenoides.</p>

      <h3>4. Reparar mientras duermes</h3>
      <p>La mayor parte de la reparación oxidativa ocurre en sueño profundo. Duerme 7-9 horas en horarios consistentes. Habitación fría y oscura. Sin pantallas 60 min antes.</p>

      <h2 id="suplementos">Los suplementos que sí funcionan</h2>
      <p>Por nivel de evidencia:</p>
      <ol>
        <li><strong>Precursores de glutatión (Immunocal, NAC):</strong> el primer escalón. Eleva el antioxidante maestro.</li>
        <li><strong>Omega-3 EPA/DHA:</strong> reduce inflamación, protege membranas neuronales. Ver <Link to="/blog/omega-gen-v-5-en-1">Omega Gen V</Link>.</li>
        <li><strong>Vitamina D3 + K2:</strong> modula sistema inmune y reduce inflamación crónica.</li>
        <li><strong>Magnesio glicinato:</strong> cofactor de cientos de reacciones, mejora sueño.</li>
        <li><strong>Coenzima Q10:</strong> antioxidante mitocondrial, especialmente útil &gt;40 años.</li>
        <li><strong>Vitamina C:</strong> antioxidante hidrosoluble, regenerada por glutatión.</li>
      </ol>

      <Callout>
        <strong>Conclusión práctica:</strong> el estrés oxidativo no se elimina (es parte de estar vivo) pero sí se <em>controla</em>. La mejor inversión: cuidar tu glutatión + reducir tóxicos + dormir bien. Lo demás son detalles.
      </Callout>

      <p>Si quieres saber exactamente qué necesitas tú, <Link to="/#quiz">haz el quiz wellness gratis</Link> y recibe un protocolo personalizado de 3 meses. Y si quieres profundizar más, lee también <Link to="/glutation">nuestra guía completa de glutatión</Link>.</p>
    </>
  ),
};

export default ARTICLE_CONTENT;
